import React, { useState, useMemo, memo } from 'react'
import styled from 'styled-components'
import { Skeleton, message } from 'antd'
import { CaseStatusEnum, REQUEST_STATUS, MODAL_MODES } from '../../../constants'

import { Container } from "../../../Styles"
import { SectionHeader } from '../../SectionHeader'
import { Header3 } from '../../../Styles'

const TreatmentsContainer = styled(Container)`
    line-height: 1.6;
`
import { RequestedMedications } from './RequestedMedications'
import { QueuedUpPrescriptions } from './QueuedUpPrescriptions'
import { PrescribedTreatments } from './PrescribedTreatments'
import { NoTemplatesAvailable } from './NoTemplatesAvailable'
import { TreatmentModal } from './TreatmentModal'
import { CompleteConsultationModal } from './CompleteConsultationModal'
import { UpdateStatusModal } from './UpdateStatusModal'
import { Labs } from './Labs'
import { LabOrderModal } from './LabOrderModal'
import { ProviderActions } from './ProviderActions'
import { RequestLabOrder } from './RequestLabOrder'

import { useCaseViewContext } from '../../../context'
import { useNavigation } from '../../../context/NavigationContext'
import { usePrescriptionTemplates } from '../../../hooks/usePrescriptionTemplates'
import { useRequestedMedications } from '../../../hooks/useRequestedMedications'
import { usePrescriptionQueue } from '../../../hooks/usePrescriptionQueue'
import { usePrescriptionSubmission } from '../../../hooks/usePrescriptionSubmission'

export const Treatments = memo(({ apiType }) => {
    const { 
        caseDetails, 
        activeRequest, 
        provider, 
        fetchCaseDetails, 
        apiClasses, 
        setChartingPrescriptions,
        readOnlyMode,
        currentPharmacyId,
    } = useCaseViewContext()
    
    const { navigateToCaseQueue } = useNavigation()
    
    const refreshPatientMessages = () => {
        window.dispatchEvent(new CustomEvent('refreshPatientMessages', {
            detail: { caseId: caseDetails?.caseId }
        }))
    }
    
    const { requestedMedications } = useRequestedMedications()
    
    const [treatmentModalMode, setTreatmentModalMode] = useState(null)
    const [selectedPrescriptionId, setSelectedPrescriptionId] = useState(null)
    const [showCompleteConsultationModal, setShowCompleteConsultationModal] = useState(false)
    const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false)
    const [statusToUpdate, setStatusToUpdate] = useState(null)
    const [showLabOrderModal, setShowLabOrderModal] = useState(false)
    
    const { 
        prescriptionQueue,
        prescriptionQueueArray,
        unTemplatizedPrescriptions,
        loading: queueLoading,
        addPrescription,
        updatePrescription,
        removePrescription: removePrescriptionFromQueue,
    } = usePrescriptionQueue({ 
        caseDetails, 
        activeRequest, 
        apiClasses,
        apiType,
        currentPharmacyId,
        setChartingPrescriptions,
        readOnlyMode,
    })
    
    const { loading: submissionLoading, completeConsultation } = usePrescriptionSubmission({ apiClasses })
    
    const { 
        templates, 
        loading: templatesLoading, 
        searchTemplates, 
        applyTemplate,
        dispenseUnits,
        getDispenseUnitById
    } = usePrescriptionTemplates({ apiClasses, apiType })
    
    const providerId = provider?.userId || provider?.userEmail || provider?._id
    const assignedProviderId = caseDetails?.assignedPhysician || caseDetails?.assignedProvider || caseDetails?.physicianId || caseDetails?.physicianEmail
    
    const isAssignedProvider = providerId === assignedProviderId
    const completedStatuses = [CaseStatusEnum.CONSULT_COMPLETE, CaseStatusEnum.CONSULT_COMPLETE_NO_RX]
    const requestIsAlreadyCompleted = activeRequest?.status === REQUEST_STATUS.COMPLETED || completedStatuses.includes(caseDetails?.status)
    
    const selectPrescription = (prescriptionId) => {
        setSelectedPrescriptionId(prescriptionId)
        setTreatmentModalMode(MODAL_MODES.EDIT)
    }
    
    const handleRemovePrescription = (prescriptionId) => {
        removePrescriptionFromQueue(prescriptionId)
    }
    
    const handleTemplateChange = async (templateData) => {
        const { template, templateAlternatives, requestedMedication, prescriptionId } = templateData
        
        const currentPrescription = prescriptionQueue[prescriptionId]
        if (!currentPrescription) {
            console.error('Prescription not found in queue:', prescriptionId)
            return
        }
        
        try {
            const updatedPrescription = await applyTemplate({
                template,
                templateAlternatives,
                requestedMedication,
                pharmacyId: template.pharmacyId || caseDetails?.pharmacyId
            })
            
            if (updatedPrescription) {
                updatedPrescription._id = currentPrescription._id
                updatePrescription(prescriptionId, updatedPrescription)
            }
        } catch (error) {
            console.error('Error applying template:', error)
        }
    }
    
    const handleReviewAndSend = () => {
        setShowCompleteConsultationModal(true)
    }

    const selectedPrescription = useMemo(() => {
        return selectedPrescriptionId ? prescriptionQueue[selectedPrescriptionId] : null
    }, [selectedPrescriptionId, prescriptionQueue])
    
    const setStatusToAdditionalInfoRequired = () => {
        setStatusToUpdate(CaseStatusEnum.ADDITIONAL_INFO_REQUIRED)
        setShowUpdateStatusModal(true)
    }
    
    const setStatusToRefer = () => {
        setStatusToUpdate(CaseStatusEnum.REFER)
        setShowUpdateStatusModal(true)
    }
    
    const setStatusToConsultCompleteNoRx = () => {
        setStatusToUpdate(CaseStatusEnum.CONSULT_COMPLETE_NO_RX)
        setShowUpdateStatusModal(true)
    }
    
    const handleCompleteConsultation = async (data) => {
        try {
            await completeConsultation({
                prescriptions: data.prescriptions,
                unTemplatizedPrescriptions: unTemplatizedPrescriptions,
                caseId: caseDetails._id,
                requestId: activeRequest._id,
                needsReview: data.needsReview,
                pin: data.pin,
                useTFA: data.useTFA,
                providerOTPCode: data.providerOTPCode,
                patientExplanation: data.patientExplanation,
                rejectionReason: data.rejectionReason,
                chartingNote: data.chartingNote,
            })
            
            if (caseDetails.fetchCaseDetails) {
                await caseDetails.fetchCaseDetails()
            }
        } catch (error) {
            console.error('Failed to complete consultation:', error)
            throw error 
        }
    }
    
    const handleTreatmentModalCancel = () => {
        setTreatmentModalMode(null)
        setSelectedPrescriptionId(null)
    }
    
    const handleTreatmentModalOk = async (prescription) => {
        if (selectedPrescriptionId) {
            updatePrescription(selectedPrescriptionId, prescription)
        } else {
            await addPrescription(prescription)
        }
        setTreatmentModalMode(null)
        setSelectedPrescriptionId(null)
    }
    
    const handleUpdateStatus = async (update) => {
        try {
            if (!apiClasses?.PhysicianAPI?.updateCaseStatus) {
                throw new Error('PhysicianAPI not available')
            }

            const payload = {
                status: update.status,
                patientExplanation: update.patientExplanation,
                sendToPatient: update.sendToPatient,
                rejectionReason: update.rejectionReason,
                requestId: update.requestId
            }

            await apiClasses.PhysicianAPI.updateCaseStatus(caseDetails?.caseId, payload)
            
            await fetchCaseDetails()
            await refreshPatientMessages()
            
            setShowUpdateStatusModal(false)
            setStatusToUpdate(null)
            
            message.success('Case status updated successfully')
            
        } catch (error) {
            console.error('Error updating case status:', error)
            message.error('Failed to update case status')
        }
    }

    if (apiType === 'admin') {
        return (
            <>
            <TreatmentsContainer>
                <SectionHeader title="Treatments" />
                
                <Labs setShowLabOrderModal={setShowLabOrderModal} />
                
                <RequestedMedications 
                    medications={activeRequest?.medicines || []}
                />
                
                <div style={{ marginTop: '20px' }}>
                    <h4 style={{ marginBottom: '10px', color: '#333' }}>Prescribed Treatments</h4>
                    <PrescribedTreatments 
                        isAssignedProvider={false}
                        isAdmin 
                    />
                </div>
            </TreatmentsContainer>
            
            {showLabOrderModal && (
                <LabOrderModal
                    closeLabOrderModal={() => setShowLabOrderModal(false)}
                />
            )}
            </>
        )
    }
    
    const shouldShowProviderActions = apiType === 'physician' && !readOnlyMode;
    
    return (
               <>
               <TreatmentsContainer>
                   <SectionHeader title="Treatments">
                       {shouldShowProviderActions && (
                           <ProviderActions
                               setActionCaseStatus={setStatusToUpdate}
                               showTreatmentModal={() => setTreatmentModalMode(MODAL_MODES.ADD)}
                               prescriptionQueue={prescriptionQueue}
                               onReviewAndSend={handleReviewAndSend}
                               onAdditionalInfoRequired={setStatusToAdditionalInfoRequired}
                               onRefer={setStatusToRefer}
                               onConsultCompleteNoRx={setStatusToConsultCompleteNoRx}
                           />
                       )}
            </SectionHeader>
            
            <Labs setShowLabOrderModal={setShowLabOrderModal} />
            <RequestLabOrder />
            
            <Skeleton loading={!caseDetails} active paragraph={{ rows: 4 }}>
                {requestedMedications.length > 0 && (
                    <RequestedMedications
                        background='rgba(204, 125, 203, 0.15)'
                        color="rgba(204, 125, 203, 1)"
                        medications={requestedMedications}
                    />
                )}

                {!requestIsAlreadyCompleted && !!unTemplatizedPrescriptions?.length && (
                    <NoTemplatesAvailable
                        medications={unTemplatizedPrescriptions}
                    />
                )}
                <QueuedUpPrescriptions 
                    prescriptions={prescriptionQueueArray}
                    isAssignedProvider={isAssignedProvider}
                    selectPrescription={selectPrescription}
                    removePrescription={handleRemovePrescription}
                    onTemplateChange={handleTemplateChange}
                    loading={queueLoading}
                />
                <Header3>Already Prescribed Treatments</Header3>
                <PrescribedTreatments 
                    isAssignedProvider={isAssignedProvider}
                />
            </Skeleton>
            
            <TreatmentModal
                mode={treatmentModalMode}
                selectedPrescriptionId={selectedPrescriptionId}
                selectedPrescription={selectedPrescription}
                onCancel={handleTreatmentModalCancel}
                onOk={handleTreatmentModalOk}
                templates={templates}
                templatesLoading={templatesLoading}
                searchTemplates={searchTemplates}
                dispenseUnits={dispenseUnits}
                getDispenseUnitById={getDispenseUnitById}
            />
            <CompleteConsultationModal
                visible={showCompleteConsultationModal}
                onCancel={() => setShowCompleteConsultationModal(false)}
                onComplete={handleCompleteConsultation}
                prescriptions={prescriptionQueueArray}
                unTemplatizedPrescriptions={unTemplatizedPrescriptions}
                loading={submissionLoading}
                onNavigateToCaseQueue={navigateToCaseQueue}
            />
            <UpdateStatusModal
                modalVisibility={showUpdateStatusModal}
                onCancel={() => {
                    setShowUpdateStatusModal(false)
                    setStatusToUpdate(null)
                }}
                onConfirm={handleUpdateStatus}
                actionCaseStatus={statusToUpdate}
                setCaseActionStatus={setStatusToUpdate}
            />
            {showLabOrderModal && (
                <LabOrderModal
                    closeLabOrderModal={() => setShowLabOrderModal(false)}
                />
            )}
        </TreatmentsContainer>
        </>
    )
})
